import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import {
  ArrowRight, ArrowDown, ChevronLeft, ChevronRight,
  Bed, Maximize, Layers, Plug, Home, Clock, Heart, Shield,
  Factory, Truck, ArrowUpDown, Anchor, Award, Settings, Package, Users, X,
  Building2, Ruler, Weight, Zap, Wind, Flame, Droplets, DoorOpen, Thermometer, Grid3x3,
} from "lucide-react";
import SEO from "@/components/SEO";

/* ─── IMAGES ─── */
const IMG = {
  hero: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/cy2u14km_1.png",
  exterior: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/yyrmemkj_LOISIR%20YONO%20140426_Sce%CC%80ne%2011.jpg",
  interiorDay: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/p4mebkj8_ChatGPT%20Image%2024%20avr.%202026%20a%CC%80%2015_39_32.png",
  interiorNight: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/6vgiuohd_LOISIR%20YONO%20140426%20%283%29%20NUIT.jpg",
  detailNight: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/5h3ci9bp_LOISIR%20YONO%20140426%20%289%29%20NUIT.jpg",
};

const GALLERY = [
  { url: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/cy2u14km_1.png", alt: "T3 - Extérieur coucher de soleil", label: "Extérieur" },
  { url: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/igif2v06_2.png", alt: "T3 - Salle d'eau vue dessus", label: "Salle d'eau" },
  { url: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/qrqd8wcg_T3.png", alt: "T3 - Coupe intérieure", label: "Coupe intérieure" },
  { url: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/wwbfhjcq_4.png", alt: "T3 - Plan vue dessus", label: "Plan" },
  { url: "https://customer-assets.emergentagent.com/job_yono-direct/artifacts/tr3fw7d1_5.png", alt: "T3 - Vue nuit", label: "Ambiance nuit" },
];

/* ─── OPTIONS ─── */
const OPTIONS = [
  { id: "terrasse", name: "Terrasse bois" },
  { id: "pergola", name: "Pergola ou auvent" },
  { id: "clim", name: "Climatisation réversible" },
  { id: "literie", name: "Pack literie" },
  { id: "mobilier", name: "Mobilier complémentaire" },
  { id: "livraison", name: "Livraison", label: "Sur devis fonction distance" },
  { id: "urbanisme", name: "Autorisation d'urbanisme" },
];

/* ─── SPECS ─── */
const SPECS = [
  { icon: Building2, label: "Type", value: "Modules bois massif habitables" },
  { icon: Bed, label: "Capacite", value: "4 personnes" },
  { icon: Layers, label: "Structure", value: "Panneaux CLT bois massif épicéa" },
  { icon: Ruler, label: "Dimensions hors tout", value: "L 2,90 m x l 5,20 m" },
  { icon: Maximize, label: "Surface interieure", value: "13,5 m\u00B2" },
  { icon: Weight, label: "Poids", value: "4 500 kg" },
  { icon: ArrowUpDown, label: "Hauteur int.", value: "Min 190 cm / Max 320 cm" },
  { icon: Grid3x3, label: "Épaisseur CLT", value: "60 mm" },
  { icon: Shield, label: "Murs + isolant", value: "140 mm" },
  { icon: Zap, label: "Puissance électrique", value: "6 kWh" },
  { icon: Truck, label: "Transport", value: "2 modules / camion" },
  { icon: Plug, label: "Réseaux", value: "Eau, élec., eaux usees / trappe technique" },
  { icon: Wind, label: "Ventilation", value: "Extracteur + clapet" },
  { icon: Flame, label: "Chauffage", value: "Électrique, PAC en option" },
  { icon: Droplets, label: "Eau chaude", value: "Ballon 50 L" },
  { icon: DoorOpen, label: "Menuiséries", value: "Bois, alu où PVC" },
  { icon: Home, label: "Revêtement ext.", value: "Bardage bois Douglas + tole" },
  { icon: Thermometer, label: "Isolation", value: "CLT 60 mm / + fibre bois" },
];


/* ─── REVEAL HOOK ─── */
function useReveal(threshold = 0.1) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return [ref, vis];
}

function Reveal({ children, className = "", delay = 0 }) {
  const [ref, vis] = useReveal();
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${vis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"} ${className}`} style={{ transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
}

/* ─── MAIN PAGE ─── */
export default function T3Page() {
  const navigate = useNavigate();
  const [selectedOpts, setSelectedOpts] = useState([]);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  const toggleOpt = (id) => setSelectedOpts((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);

  const handleCommander = useCallback(() => {
    const opts = OPTIONS.filter((o) => selectedOpts.includes(o.id));
    localStorage.setItem("yono_order", JSON.stringify({
      product: "HPA T3",
      selectedOptions: opts.map((o) => ({ name: o.name })),
      quantity: 1,
    }));
    navigate("/commander");
  }, [selectedOpts, navigate]);

  /* Gallery keyboard nav */
  useEffect(() => {
    if (!galleryOpen) return;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") setActiveImg((p) => (p - 1 + GALLERY.length) % GALLERY.length);
      if (e.key === "ArrowRight") setActiveImg((p) => (p + 1) % GALLERY.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [galleryOpen]);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div data-testid="t3-page">
      <SEO title="T3 — 4 couchages de plain-pied" description="Le T3 YONO : habitat bois 4 couchages de plain-pied. Tout le confort essentiel sur un seul niveau, sans mezzanine. Prix sur demande." path="/produits/t3" />

      {/* ═══════ 1. HERO ═══════ */}
      <section id="hero" className="hidden md:block relative h-screen min-h-[600px] max-h-[900px]">
        <img src={IMG.hero} alt="YONO T3" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/25 to-black/10" />
        <div className="relative z-10 h-full flex flex-col justify-end px-6 md:px-12 lg:px-24 pb-16 md:pb-24">
          <div className="max-w-3xl">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/70">YONO</span>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white mt-2 mb-3">T3</h1>
            <p className="font-body text-xl md:text-2xl text-white/95 font-medium mb-2">4 couchages de plain-pied</p>
            <p className="font-body text-sm md:text-base text-white/80 max-w-xl mb-8 leading-relaxed">
              Une solution bois tout équipée pour 4 personnes, pensée sur un seul niveau. Sans mezzanine,
              elle réunit kitchenette, salle d'eau et couchages dans une implantation simple, compacte et facile à vivre.
            </p>
            {/* Badges */}
            <div className="flex flex-wrap gap-3 mb-8">
              {[
                { icon: Bed, label: "4 couchages" },
                { icon: Maximize, label: "Plain-pied" },
                { icon: Layers, label: "CLT 60 mm" },
                { icon: Plug, label: "Prêt à raccorder" },
              ].map((b, i) => (
                <div key={i} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 px-4 py-2">
                  <b.icon className="h-4 w-4 text-white/80" strokeWidth={1.5} />
                  <span className="font-mono text-xs text-white/80 uppercase tracking-wider">{b.label}</span>
                </div>
              ))}
            </div>
            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <button data-testid="hero-commander-btn" onClick={handleCommander}
                className="flex items-center gap-2 bg-white text-soft-black px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood hover:text-white transition-colors duration-300">
                Demander un devis <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <button data-testid="hero-discover-btn" onClick={() => scrollTo("value")}
                className="flex items-center gap-2 border border-white/40 text-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-white/10 transition-colors duration-300">
                Découvrir le produit <ArrowDown className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Mobile hero */}
      <section className="md:hidden pt-16">
        <div className="w-full aspect-[4/3] overflow-hidden">
          <img src={IMG.hero} alt="YONO T3" className="w-full h-full object-cover" />
        </div>
        <div className="px-5 py-6">
          <span className="font-mono text-xs uppercase tracking-[0.15em] text-soft-black/40">YONO</span>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-soft-black mt-1 mb-2">T3</h1>
          <p className="font-body text-lg text-soft-black mb-3 font-medium">4 couchages de plain-pied</p>
          <p className="font-body text-base text-soft-black/70 mb-6 leading-relaxed">Une solution bois tout équipée pour 4 personnes, pensée sur un seul niveau. Sans mezzanine,
              elle réunit kitchenette, salle d'eau et couchages dans une implantation simple, compacte et facile à vivre.</p>
          <div className="flex flex-col gap-2.5">
            <button data-testid="t3-hero-discover-btn-mobile" onClick={() => scrollTo("t3-value")}
              className="flex items-center justify-center gap-2 border border-soft-black text-soft-black px-6 py-3.5 font-body text-xs uppercase tracking-[0.15em]">
              Découvrir le produit <ArrowDown className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </section>

      {/* ═══════ 2. PRODUCT VALUE ═══════ */}
      <section id="value" className="py-14 md:py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Le produit</span>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-soft-black mt-4 mb-6 max-w-2xl">
              Tout le confort essentiel sur un seul niveau
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="font-body text-base md:text-lg text-soft-black/70 leading-relaxed max-w-2xl mb-8">
              Ce modèle de plain-pied a été conçu pour accueillir jusqu'a 4 personnes sans mezzanine,
              avec une organisation simple et fonctionnelle. Il réunit les couchages, la kitchenette et la salle d'eau
              sur un seul niveau, dans une implantation compacte qui privilégie la facilite d'usage, l'accessibilité
              et la robustesse au quotidien.
            </p>
          </Reveal>
          <Reveal delay={200}>
            <div className="w-full overflow-hidden">
              <img src={IMG.interiorDay} alt="HPA T3 interieur" className="w-full h-auto object-cover img-zoom" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ 3. KEY FEATURES ═══════ */}
      <section className="py-14 md:py-16 px-6 md:px-12 lg:px-24 bg-light-beige">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Avantages</span>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-soft-black mt-4 mb-8">Points forts</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-warm-grey/30 border border-warm-grey/30">
            {[
              { icon: Home, title: "Architecture bois sobre et chaleureuse", text: "Un design épuré qui s'intégré naturellement dans son environnement." },
              { icon: Clock, title: "Mise en place rapide sur site", text: "Fabrique en atelier et livre prêt à raccorder pour une installation simplifiée." },
              { icon: Heart, title: "Confort d'usage et entretien simplifié", text: "Des matériaux et finitions pensés pour le quotidien et la durabilité." },
              { icon: Shield, title: "Pense pour une utilisation durable", text: "Une conception robuste et dès composants de qualité pour le long terme." },
            ].map((f, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="bg-light-beige p-8 md:p-10 h-full">
                  <f.icon className="h-6 w-6 text-natural-wood mb-6" strokeWidth={1.5} />
                  <h3 className="font-heading text-lg font-medium tracking-tight text-soft-black mb-3">{f.title}</h3>
                  <p className="font-body text-sm text-soft-black/60 leading-relaxed">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 4. INTERIOR LAYOUT ═══════ */}
      <section className="py-14 md:py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <Reveal>
                <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Implantation</span>
                <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-soft-black mt-4 mb-6">
                  Une organisation simple sur un seul niveau
                </h2>
              </Reveal>
              <Reveal delay={100}>
                <p className="font-body text-soft-black/70 leading-relaxed mb-8">
                  Ce modèle de plain-pied réunit toutes les fonctions sur un seul niveau, sans mezzanine.
                  L'implantation privilégie la facilite d'usage, l'accessibilité et la robustesse, avec un aménagement
                  compact conçu pour accueillir jusqu'a 4 personnes dans un espace maîtrisé.
                </p>
              </Reveal>
              <Reveal delay={200}>
                <ul className="space-y-4">
                  {[
                    "Sejour compact avec kitchenette intégrée",
                    "Couchages organises de plain-pied",
                    "Coin repas / espace de vie optimisé",
                    "Salle d'eau avec douche et WC",
                    "Rangements intégrés selon configuration",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 bg-natural-wood mt-2 shrink-0" />
                      <span className="font-body text-sm text-soft-black/70">{item}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <Reveal delay={150}>
              <div className="overflow-hidden">
                <img src="https://customer-assets.emergentagent.com/job_yono-direct/artifacts/9a4t0cay_T3.png" alt="T3 plan interieur" className="w-full h-auto object-cover img-zoom" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ═══════ 5. TECHNICAL SPECS ═══════ */}
      <section id="specs" className="py-14 md:py-16 px-6 md:px-12 lg:px-24 bg-light-beige">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Fiche technique</span>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-soft-black mt-4 mb-8">Caracteristiques techniques</h2>
          </Reveal>
          <Reveal delay={100}>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-warm-grey/30 border border-warm-grey/30">
              {SPECS.map((spec, i) => (
                <div key={i} className="bg-light-beige p-5 md:p-6 flex flex-col items-start">
                  <spec.icon className="h-5 w-5 text-natural-wood mb-3" strokeWidth={1.5} />
                  <span className="font-mono text-[10px] uppercase tracking-wider text-soft-black/40 mb-1.5">{spec.label}</span>
                  <span className="font-body text-xs text-soft-black font-medium leading-tight">{spec.value}</span>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ 6. OPTIONS ═══════ */}
      <section id="options" className="py-14 md:py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Personnalisation</span>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-soft-black mt-4 mb-8">Options disponibles</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {OPTIONS.map((opt, i) => (
              <Reveal key={opt.id} delay={i * 60}>
                <button
                  data-testid={`option-${opt.id}`}
                  onClick={() => toggleOpt(opt.id)}
                  className={`w-full text-left border p-6 transition-all duration-300 cursor-pointer ${
                    selectedOpts.includes(opt.id)
                      ? "border-soft-black bg-soft-black text-off-white"
                      : "border-warm-grey/30 hover:border-warm-grey bg-off-white text-soft-black"
                  }`}
                >
                  <h4 className="font-heading text-sm font-medium mb-3">{opt.name}</h4>
                  <span className={`font-mono text-xs ${selectedOpts.includes(opt.id) ? "text-off-white/70" : "text-soft-black/50"}`}>
                    {opt.label || "Sur devis"}
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
          {/* Price update */}
          <Reveal delay={300}>
            <div className="mt-12 border-t border-warm-grey/30 pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <span className="font-mono text-xs uppercase tracking-wider text-soft-black/50">Tarif</span>
                <div data-testid="options-price-on-request" className="font-heading text-3xl md:text-4xl tracking-tight text-soft-black mt-1">Prix sur demande</div>
              </div>
              <button data-testid="options-commander-btn" onClick={handleCommander}
                className="flex items-center gap-2 bg-soft-black text-off-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood transition-colors duration-300">
                Demander un devis <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ 7. FABRICATION ═══════ */}
      <section className="py-14 md:py-16 px-6 md:px-12 lg:px-24 bg-light-beige">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Mise en oeuvre</span>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-soft-black mt-4 mb-8">Simple à mettre en oeuvre</h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-warm-grey/30 border border-warm-grey/30">
            {[
              { icon: Factory, title: "Fabrication en atelier", text: "Prefabrication complete en environnement contrôle pour une qualité constante." },
              { icon: Truck, title: "Livre prêt à raccorder", text: "Modules transportes par camion et installes directement sur site." },
              { icon: ArrowUpDown, title: "Passage de fourche renforcé", text: "Structure dimensionnée pour la manutention par chariot élévateur." },
              { icon: Anchor, title: "Pose sur pieds metalliques reglables", text: "Installation sans fondation lourde, adaptable au terrain." },
            ].map((f, i) => (
              <Reveal key={i} delay={i * 100}>
                <div className="bg-light-beige p-8 md:p-10 h-full">
                  <f.icon className="h-6 w-6 text-natural-wood mb-6" strokeWidth={1.5} />
                  <h3 className="font-heading text-base font-medium tracking-tight text-soft-black mb-3">{f.title}</h3>
                  <p className="font-body text-sm text-soft-black/60 leading-relaxed">{f.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={300}>
            <p className="font-body text-soft-black/70 mt-8 max-w-2xl">
              Le HPA T3 est pense pour une mise en place rapide, une logistique simplifiée et une exploitation facile au quotidien.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══════ 8. GALLERY ═══════ */}
      <section id="t3-gallery" className="py-14 md:py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Visuels</span>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-soft-black mt-4 mb-8">Galerie</h2>
          </Reveal>

          {/* Slideshow */}
          <div className="relative">
            <div className="overflow-hidden">
              <div className="aspect-[16/9] md:aspect-[21/9] relative cursor-pointer" onClick={() => setGalleryOpen(true)}>
                <img
                  data-testid="t3-gallery-slide"
                  src={GALLERY[activeImg]?.url}
                  alt={GALLERY[activeImg]?.alt}
                  className="w-full h-full object-cover transition-opacity duration-500"
                />
                <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/40 to-transparent">
                  <span className="font-mono text-sm uppercase tracking-wider text-white">{GALLERY[activeImg]?.label}</span>
                  <span className="font-mono text-xs text-white/50 ml-4">{activeImg + 1} / {GALLERY.length}</span>
                </div>
              </div>
            </div>

            {/* Navigation arrows */}
            <button data-testid="t3-slide-prev" onClick={() => setActiveImg((p) => (p - 1 + GALLERY.length) % GALLERY.length)}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/80 hover:bg-white transition-colors">
              <ChevronLeft className="h-5 w-5 text-soft-black" />
            </button>
            <button data-testid="t3-slide-next" onClick={() => setActiveImg((p) => (p + 1) % GALLERY.length)}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 flex items-center justify-center bg-white/80 hover:bg-white transition-colors">
              <ChevronRight className="h-5 w-5 text-soft-black" />
            </button>

            {/* Thumbnails */}
            <div className="flex gap-2 mt-3">
              {GALLERY.map((img, i) => (
                <button key={i} data-testid={`t3-thumb-${i}`} onClick={() => setActiveImg(i)}
                  className={`flex-1 aspect-[4/3] overflow-hidden border-2 transition-all ${i === activeImg ? "border-soft-black" : "border-transparent opacity-50 hover:opacity-80"}`}>
                  <img src={img.url} alt={img.alt} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Lightbox */}
        <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
          <DialogContent className="max-w-6xl p-0 border-none bg-soft-black rounded-none overflow-hidden [&>button]:hidden">
            <DialogTitle className="sr-only">Galerie T3</DialogTitle>
            <DialogDescription className="sr-only">Navigation dans les visuels</DialogDescription>
            <div className="relative">
              <img src={GALLERY[activeImg]?.url} alt={GALLERY[activeImg]?.alt} className="w-full max-h-[80vh] object-contain" />
              <button data-testid="t3-gallery-close" onClick={() => setGalleryOpen(false)}
                className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors">
                <X className="h-5 w-5 text-white" />
              </button>
              <button data-testid="t3-gallery-prev" onClick={() => setActiveImg((p) => (p - 1 + GALLERY.length) % GALLERY.length)}
                className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors">
                <ChevronLeft className="h-5 w-5 text-white" />
              </button>
              <button data-testid="t3-gallery-next" onClick={() => setActiveImg((p) => (p + 1) % GALLERY.length)}
                className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/10 hover:bg-white/20 transition-colors">
                <ChevronRight className="h-5 w-5 text-white" />
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                {GALLERY.map((_, i) => (
                  <button key={i} onClick={() => setActiveImg(i)}
                    className={`w-2 h-2 transition-all ${i === activeImg ? "bg-white scale-125" : "bg-white/40"}`} />
                ))}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </section>

      {/* ═══════ 9. PURCHASE / CONFIGURATION ═══════ */}
      <section id="purchase" className="py-14 md:py-16 px-6 md:px-12 lg:px-24 bg-light-beige">
        <div className="max-w-3xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Devis</span>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-soft-black mt-4 mb-8">T3</h2>
          </Reveal>
          <Reveal delay={100}>
            <p className="font-body text-soft-black/60 mb-8">
              Habitat bois 4 couchages &mdash; 13 m&sup2; &mdash; Structure CLT 60 mm &mdash; Prêt à raccorder
            </p>
          </Reveal>
          <Reveal delay={150}>
            <div className="border border-warm-grey/40 bg-off-white">
              {selectedOpts.length > 0 && (
                <div className="divide-y divide-warm-grey/20">
                  {OPTIONS.filter((o) => selectedOpts.includes(o.id)).map((opt) => (
                    <div key={opt.id} className="p-6 md:px-8 flex justify-between items-center">
                      <span className="font-body text-sm text-soft-black/70">{opt.name}</span>
                      <span className="font-mono text-xs text-soft-black/50">{opt.label || "Sur devis"}</span>
                    </div>
                  ))}
                </div>
              )}
              <div className="p-6 md:p-8 flex justify-between items-center border-t border-soft-black bg-soft-black text-off-white">
                <span className="font-heading text-sm uppercase tracking-wider">Tarif</span>
                <span data-testid="purchase-price-on-request" className="font-heading text-2xl md:text-3xl tracking-tight">Prix sur demande</span>
              </div>
            </div>
          </Reveal>
          <Reveal delay={200}>
            <div className="flex flex-col sm:flex-row gap-4 mt-8">
              <button data-testid="purchase-commander-btn" onClick={handleCommander}
                className="flex-1 flex items-center justify-center gap-2 bg-soft-black text-off-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood transition-colors duration-300">
                Demander un devis <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <button data-testid="purchase-info-btn" onClick={() => navigate("/contact?product=HPA+T3")}
                className="flex-1 flex items-center justify-center gap-2 border border-soft-black text-soft-black px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-soft-black hover:text-off-white transition-colors duration-300">
                Demander des informations
              </button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══════ 10. REASSURANCE ═══════ */}
      <section className="py-14 md:py-16 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-mono text-sm uppercase tracking-[0.15em] text-soft-black/40">Engagements</span>
            <h2 className="font-heading text-4xl sm:text-5xl tracking-tight text-soft-black mt-4 mb-8">Pourquoi choisir YONO</h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-warm-grey/30 border border-warm-grey/30">
            {[
              { icon: Layers, title: "Conception bois massif CLT", text: "Materiaux biosourcés et techniques éprouvées." },
              { icon: Award, title: "Qualité de fabrication", text: "Prefabrication en atelier avec contrôle qualité rigoureux." },
              { icon: Home, title: "Design sobre et durable", text: "Une esthétique intemporelle qui vieillit bien." },
              { icon: Settings, title: "Entretien simplifié", text: "Des matériaux et finitions faciles a entretenir." },
              { icon: Package, title: "Livraison prete à raccorder", text: "Modules livres complets, installation rapide." },
              { icon: Users, title: "Accompagnement avant commande", text: "Une équipé disponible pour répondre à vos questions." },
            ].map((r, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="bg-off-white p-8 md:p-10 h-full">
                  <r.icon className="h-5 w-5 text-natural-wood mb-5" strokeWidth={1.5} />
                  <h3 className="font-heading text-sm font-medium tracking-tight text-soft-black mb-2">{r.title}</h3>
                  <p className="font-body text-xs text-soft-black/50 leading-relaxed">{r.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════ 11. FINAL CTA ═══════ */}
      <section className="relative py-14 md:py-16 overflow-hidden">
        <img src={IMG.exterior} alt="YONO T3" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/30" />
        <div className="relative z-10 px-6 md:px-12 lg:px-24 text-center">
          <Reveal>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white max-w-3xl mx-auto mb-8">
              Envie d'installer un habitat bois simple, beau et prêt à vivre ?
            </h2>
          </Reveal>
          <Reveal delay={100}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button data-testid="cta-commander-btn" onClick={handleCommander}
                className="flex items-center justify-center gap-2 bg-white text-soft-black px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-natural-wood hover:text-white transition-colors duration-300">
                Commander le HPA T3 <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <button data-testid="cta-contact-btn" onClick={() => navigate("/contact")}
                className="flex items-center justify-center gap-2 border border-white/40 text-white px-8 py-4 font-body text-xs uppercase tracking-[0.15em] hover:bg-white/10 transition-colors duration-300">
                Nous contacter
              </button>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
